import * as Sentry from '@sentry/react';
import { Component, type ErrorInfo, type ReactNode } from 'react';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';

const preloadErrors = new WeakSet<object>();
const reloadStorageKey = 'deployment-update:last-reload';
const reloadVersionKey = 'deployment-update:last-reload-version';
const reloadCooldownMs = 60_000;
let preloadFailed = false;
const isOffline = () => navigator.onLine === false;

// Remember failures even when the first route fails before React mounts.
// Let React receive the original error so its boundary can show a fallback.
window.addEventListener('vite:preloadError', (event) => {
  const { payload } = event as Event & { payload?: unknown };
  if (payload && typeof payload === 'object') preloadErrors.add(payload);
  preloadFailed = true;
});

type Props = { children: ReactNode };
type State = {
  updateAvailable: boolean;
  failed: boolean;
  loadFailed: boolean;
  offline: boolean;
  connectionFailed: boolean;
};

export default class DeploymentUpdateBoundary extends Component<Props, State> {
  private versionInterval?: number;

  private versionRequest?: AbortController;

  private mounted = false;

  private reloadRequested = false;

  // Any error shows the fallback; only import failures allow automatic recovery.
  static getDerivedStateFromError(error: Error): Partial<State> {
    return preloadErrors.has(error) ? { failed: true, loadFailed: true } : { failed: true };
  }

  constructor(props: Props) {
    super(props);
    this.state = {
      updateAvailable: false,
      failed: false,
      loadFailed: preloadFailed,
      offline: isOffline(),
      connectionFailed: false,
    };
  }

  componentDidMount() {
    this.mounted = true;
    window.addEventListener('vite:preloadError', this.handlePreloadError);
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
    if (import.meta.env.PROD) {
      this.checkVersion();
      this.versionInterval = window.setInterval(this.handleVersionCheck, 60_000);
      document.addEventListener('visibilitychange', this.handleVersionCheck);
      window.addEventListener('focus', this.handleVersionCheck);
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
    this.checkVersion();
  }

  componentWillUnmount() {
    this.mounted = false;
    window.removeEventListener('vite:preloadError', this.handlePreloadError);
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
    window.clearInterval(this.versionInterval);
    document.removeEventListener('visibilitychange', this.handleVersionCheck);
    window.removeEventListener('focus', this.handleVersionCheck);
    this.versionRequest?.abort();
  }

  private checkVersion = async () => {
    const { updateAvailable, loadFailed, connectionFailed } = this.state;
    if (
      !this.mounted ||
      this.versionRequest ||
      document.visibilityState !== 'visible' ||
      (updateAvailable && !loadFailed && !connectionFailed)
    ) return;

    if (isOffline()) {
      this.setState({ offline: true });
      return;
    }

    const controller = new AbortController();
    this.versionRequest = controller;
    const timeout = setTimeout(() => controller.abort(), 10_000);
    try {
      const response = await fetch(`${import.meta.env.BASE_URL}version.json`, {
        cache: 'no-store',
        signal: controller.signal,
        redirect: 'error',
      });
      if (!response.ok) throw new Error('Version check unavailable');
      const data: unknown = await response.json();
      if (
        !data ||
        typeof data !== 'object' ||
        !('version' in data) ||
        typeof data.version !== 'string' ||
        !data.version.trim()
      ) throw new Error('Invalid version metadata');
      if (!this.mounted || controller.signal.aborted || isOffline()) return;

      const changed = data.version !== import.meta.env.VITE_BUILD_ID;
      const { loadFailed: currentLoadFailed } = this.state;
      this.setState({ updateAvailable: changed, offline: false, connectionFailed: false });
      // Automatic recovery requires both a failed import and a confirmed new build.
      if (changed && currentLoadFailed) this.reloadPage(true, data.version);
    } catch {
      if (this.mounted && this.versionRequest === controller) {
        this.setState({
          connectionFailed: true,
          offline: isOffline(),
        });
      }
    } finally {
      clearTimeout(timeout);
      if (this.versionRequest === controller) this.versionRequest = undefined;
    }
  };

  private handleVersionCheck = () => {
    this.checkVersion();
  };

  private handleOnline = () => {
    this.setState({ offline: false }, this.handleVersionCheck);
  };

  private handleOffline = () => {
    this.versionRequest?.abort();
    this.versionRequest = undefined;
    this.setState({ offline: true });
  };

  private handlePreloadError = () => {
    this.setState({ loadFailed: true }, this.handleVersionCheck);
  };

  // A click reloads without asking version.json; reloading the same build also clears React.lazy's cached rejection.
  private handleReload = () => {
    this.reloadPage();
  };

  reloadPage = (automatic = false, version = import.meta.env.VITE_BUILD_ID) => {
    if (isOffline() || this.reloadRequested) return;
    if (automatic) {
      // Limit recovery to one automatic reload per target build and browser tab.
      try {
        const lastReload = Number(window.sessionStorage.getItem(reloadStorageKey));
        const now = Date.now();
        if (window.sessionStorage.getItem(reloadVersionKey) === version) return;
        if (lastReload && now - lastReload < reloadCooldownMs) return;
        window.sessionStorage.setItem(reloadStorageKey, String(now));
        window.sessionStorage.setItem(reloadVersionKey, version);
      } catch {
        // Keep the explicit retry available when browser storage is blocked.
        return;
      }
    }
    this.reloadRequested = true;
    this.performReload();
  };

  // eslint-disable-next-line class-methods-use-this
  performReload = () => window.location.reload();

  render() {
    const { children } = this.props;
    const { updateAvailable, failed, loadFailed, offline, connectionFailed } = this.state;
    let buttonLabel = updateAvailable ? 'Atualizar agora' : 'Tentar novamente';
    if (connectionFailed) buttonLabel = 'Recarregar página';
    const failureMessage = connectionFailed
      ? 'Não foi possível verificar a versão do portal. Você pode recarregar a página. Alterações não salvas serão perdidas.'
      : 'Não foi possível carregar esta página. Tente novamente.';
    const message = updateAvailable && !connectionFailed
      ? 'Nova versão disponível. Salve seu trabalho antes de atualizar.'
      : failureMessage;
    return (
      <>
        {failed ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" disabled={offline} onClick={this.handleReload}>
                {buttonLabel}
              </Button>
            }
          >
            {failureMessage}
          </Alert>
        ) : children}
        <Snackbar open={offline} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert severity="warning">Você está sem internet. Verifique sua conexão.</Alert>
        </Snackbar>
        <Snackbar
          open={updateAvailable || (loadFailed && !failed)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            severity={connectionFailed || loadFailed ? 'warning' : 'info'}
            action={
              <Button color="inherit" disabled={offline} onClick={this.handleReload}>
                {buttonLabel}
              </Button>
            }
          >
            {message}
          </Alert>
        </Snackbar>
      </>
    );
  }
}
